import React, {
    useState, useEffect, useCallback, useRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import {
    colors, space, mediaQueries, site, zIndices
} from 'style/config';
import Chevron from 'components/Chevron';
import localeUtils from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import StickyNavBarEN from 'components/ProductPage/StickyNavBar/locales/StickyNavBar_en_US';
import StickyNavBarFR from 'components/ProductPage/StickyNavBar/locales/StickyNavBar_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(StickyNavBarEN, StickyNavBarFR);

const SECTIONS = [
    { id: 'top', locKey: 'top', targetId: null },
    { id: 'details', locKey: 'details', targetId: 'details' },
    { id: 'similar', locKey: 'similar', targetId: 'similar' },
    { id: 'qanda', locKey: 'qAndA', targetId: 'QandA' },
    { id: 'reviews', locKey: 'reviews', targetId: 'ratings-reviews-container' }
];

const SCROLL_THRESHOLD = 400;
const NAV_HEIGHT = 44;
const SCROLL_OFFSET = 10;
const ACTIVE_SECTION_THRESHOLD = NAV_HEIGHT + 16;

function StickyNavBar({ experience, productName, brandName, productImage }) {
    const [isVisible, setIsVisible] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const rafId = useRef(null);

    const updateScrollState = useCallback(() => {
        setIsVisible(window.scrollY > SCROLL_THRESHOLD);

        let current = null;

        for (let i = SECTIONS.length - 1; i >= 0; i--) {
            const section = SECTIONS[i];

            if (section.targetId) {
                const el = document.getElementById(section.targetId);

                if (el && el.getBoundingClientRect().top <= ACTIVE_SECTION_THRESHOLD) {
                    current = section.id;

                    break;
                }
            }
        }

        setActiveSection(current);
    }, []);

    const handleScroll = useCallback(() => {
        if (rafId.current) {
            return;
        }

        rafId.current = requestAnimationFrame(() => {
            rafId.current = null;
            updateScrollState();
        });
    }, [updateScrollState]);

    useEffect(() => {
        updateScrollState();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);

            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, [handleScroll, updateScrollState]);

    if (!isVisible) {
        return null;
    }

    const scrollTo = (targetId, sectionId) => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                actionInfo: `sticky_nav:${sectionId}`,
                linkName: 'D=c55'
            }
        });

        if (!targetId) {
            window.scrollTo({ top: 0, behavior: 'smooth' });

            return;
        }

        const el = document.getElementById(targetId);

        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT - SCROLL_OFFSET;

            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <Box
            position='fixed'
            top={0}
            left={0}
            right={0}
            zIndex={zIndices.dropdown}
            backgroundColor='white'
            boxShadow='light'
            baseCss={styles.root}
            role='navigation'
            aria-label={getText('navLabel')}
        >
            <Flex
                alignItems='center'
                justifyContent='center'
                height={NAV_HEIGHT}
                paddingX={4}
                maxWidth={site.maxWidth}
                marginX='auto'
                gap={6}
            >
                <Flex
                    alignItems='center'
                    gap={5}
                >
                    {SECTIONS.map(section => {
                        if (section.id === 'top') {
                            return (
                                <button
                                    key={section.id}
                                    type='button'
                                    onClick={() => scrollTo(section.targetId, section.id)}
                                    css={styles.topButton}
                                >
                                    <Chevron
                                        direction='up'
                                        size='.65em'
                                    />
                                    <span>{getText(section.locKey)}</span>
                                </button>
                            );
                        }

                        return (
                            <Text
                                key={section.id}
                                is='button'
                                type='button'
                                fontSize='sm'
                                fontWeight={activeSection === section.id ? 'bold' : 'normal'}
                                color={colors.black}
                                onClick={() => scrollTo(section.targetId, section.id)}
                                baseCss={[styles.navLink, activeSection === section.id && styles.activeLink]}
                                children={getText(section.locKey)}
                            />
                        );
                    })}
                </Flex>
                {experience?.challengerTwo && (
                    <Flex
                        alignItems='center'
                        gap={3}
                        baseCss={styles.productInfo}
                    >
                        {productImage && (
                            <img
                                src={productImage}
                                alt={productName}
                                css={styles.productImage}
                            />
                        )}
                        <Box baseCss={styles.productDetails}>
                            <Text
                                fontSize='sm'
                                numberOfLines={1}
                            >
                                <Text
                                    is='span'
                                    fontWeight='bold'
                                >
                                    {brandName}
                                </Text>{' '}
                                {productName}
                            </Text>
                        </Box>
                    </Flex>
                )}
            </Flex>
        </Box>
    );
}

const styles = {
    root: {
        transition: 'transform 0.3s ease'
    },
    topButton: {
        background: colors.nearWhite,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: `0 ${space[4]}px`,
        height: '100%',
        whiteSpace: 'nowrap',
        fontSize: '14px',
        color: colors.black
    },
    navLink: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: `${space[2]}px 0`,
        borderBottom: '2px solid transparent',
        whiteSpace: 'nowrap',
        '&:hover': {
            borderBottomColor: colors.black
        }
    },
    activeLink: {
        borderBottomColor: colors.black
    },
    productInfo: {
        borderLeft: `1px solid ${colors.midGray}`,
        paddingLeft: space[5],
        maxWidth: 250,
        overflow: 'hidden',
        [mediaQueries.xsMax]: {
            display: 'none'
        }
    },
    productImage: {
        width: 36,
        height: 36,
        objectFit: 'cover',
        borderRadius: 2,
        flexShrink: 0
    },
    productDetails: {
        overflow: 'hidden',
        minWidth: 0
    }
};

export default wrapFunctionalComponent(StickyNavBar, 'StickyNavBar');
